/**
 * emailDeliveryStatus.js
 * ----------------------
 * Single source of truth for mapping email delivery status values from
 * Firestore messageHistory documents to user-facing labels, tones, and
 * icons for the clinician Activity Log.
 *
 * Why this exists:
 * The SendGrid Event Webhook writes delivery lifecycle events (delivered,
 * bounced, dropped, etc.) onto each messageHistory document. The Activity
 * Log dialog needs to present these honestly to clinicians ("Delivered"
 * vs "Not delivered") with appropriate visual weight. Centralising the
 * mapping here means every surface that shows delivery status uses the
 * same labels and colours, and a junior developer can update the copy in
 * one place.
 *
 * Design rules applied:
 * - Semantic colour: green = positive/complete, amber = attention/open,
 *   red = error/stop. Each use carries colour + icon + text label.
 * - Sentence case everywhere.
 * - 16px floor for content; these labels appear at text-xs (metadata).
 * - Professional surface only (no gold).
 */

/**
 * Delivery status constants matching the values written by the backend
 * (_send_email_and_log seeds these, the webhook updates them).
 */
export const DELIVERY_STATUS = {
  SEND_FAILED: 'send_failed',
  SENT: 'sent',
  PROCESSED: 'processed',
  DEFERRED: 'deferred',
  DELIVERED: 'delivered',
  PARTIALLY_DELIVERED: 'partially_delivered',
  BOUNCED: 'bounced',
  DROPPED: 'dropped',
  SPAM_REPORTED: 'spam_reported',
};

/**
 * Tone values used to select the right semantic colour in the UI.
 * Maps to the design system's semantic colour rules (green/amber/red).
 */
export const TONE = {
  POSITIVE: 'positive',
  ATTENTION: 'attention',
  NEGATIVE: 'negative',
  NEUTRAL: 'neutral',
};

/**
 * Maps a delivery status string to a presentation object for the Activity Log.
 *
 * @param {string|null|undefined} status - The deliveryStatus field from Firestore.
 * @returns {{ label: string, tone: string, icon: string }}
 *   - label: Sentence-case text shown next to the badge (e.g. "Delivered").
 *   - tone: One of TONE values, used to pick semantic colour.
 *   - icon: A short unicode/emoji-free descriptor for the icon slot
 *           (the component maps this to the actual icon element).
 */
export function getDeliveryStatusPresentation(status) {
  switch (status) {
    case DELIVERY_STATUS.DELIVERED:
      return { label: 'Delivered', tone: TONE.POSITIVE, icon: 'check' };

    case DELIVERY_STATUS.PARTIALLY_DELIVERED:
      return { label: 'Partly delivered', tone: TONE.ATTENTION, icon: 'warning' };

    case DELIVERY_STATUS.SENT:
      return { label: 'Sent, awaiting confirmation', tone: TONE.NEUTRAL, icon: 'clock' };

    case DELIVERY_STATUS.PROCESSED:
      return { label: 'Sent, awaiting confirmation', tone: TONE.NEUTRAL, icon: 'clock' };

    case DELIVERY_STATUS.DEFERRED:
      return { label: 'Delivery delayed, retrying', tone: TONE.ATTENTION, icon: 'clock' };

    case DELIVERY_STATUS.BOUNCED:
      return { label: 'Not delivered', tone: TONE.NEGATIVE, icon: 'error' };

    case DELIVERY_STATUS.DROPPED:
      return { label: 'Not delivered', tone: TONE.NEGATIVE, icon: 'error' };

    case DELIVERY_STATUS.SPAM_REPORTED:
      return { label: 'Not delivered', tone: TONE.NEGATIVE, icon: 'error' };

    case DELIVERY_STATUS.SEND_FAILED:
      return { label: 'Not sent', tone: TONE.NEGATIVE, icon: 'error' };

    default:
      // Legacy documents written before the webhook was deployed have no
      // deliveryStatus field. Show an honest neutral state.
      return describeLegacy();
  }
}

/**
 * Returns the presentation for legacy messageHistory docs that predate
 * the delivery tracking feature. Called when deliveryStatus is absent.
 *
 * @returns {{ label: string, tone: string, icon: string }}
 */
export function describeLegacy() {
  return { label: 'Sent, delivery not tracked', tone: TONE.NEUTRAL, icon: 'none' };
}

/**
 * Returns a short label for a per-recipient status, suitable for
 * rendering in a compact row within the Activity Log.
 *
 * @param {string} status - Per-recipient status (e.g. 'delivered', 'bounced').
 * @returns {string} Sentence-case label.
 */
export function getRecipientStatusLabel(status) {
  switch (status) {
    case 'delivered':
      return 'Delivered';
    case 'bounced':
      return 'Bounced';
    case 'dropped':
      return 'Not delivered';
    case 'spam_reported':
      return 'Spam reported';
    case 'deferred':
      return 'Delayed';
    case 'processed':
      return 'Processing';
    case 'sent':
      return 'Sent';
    case 'send_failed':
      return 'Not sent';
    default:
      return 'Unknown';
  }
}

/**
 * Maps a tone value to Tailwind CSS classes for the status badge.
 * Uses the design system's semantic colour rules:
 * - green for positive/complete
 * - amber for attention/open
 * - red for error/stop
 * - gray for neutral/unknown
 *
 * @param {string} tone - One of TONE values.
 * @returns {{ bg: string, text: string, border: string }}
 */
export function getToneClasses(tone) {
  switch (tone) {
    case TONE.POSITIVE:
      return { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' };
    case TONE.ATTENTION:
      return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' };
    case TONE.NEGATIVE:
      return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
    case TONE.NEUTRAL:
    default:
      return { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' };
  }
}
